// Aprovações pendentes de Gestão em Lote — 2ª etapa para lotes grandes
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ShieldAlert, ArrowLeft, Check, X, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";

const STATUS_META = {
  pending:  { cls: "bg-amber-50 text-amber-800 border-amber-300", label: "Pendente" },
  approved: { cls: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Aprovado" },
  executed: { cls: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Executado" },
  rejected: { cls: "bg-rose-50 text-rose-800 border-rose-300", label: "Rejeitado" },
  failed:   { cls: "bg-rose-50 text-rose-800 border-rose-300", label: "Falhou" },
  expired:  { cls: "bg-slate-100 text-slate-700 border-slate-300", label: "Expirado" },
};

export default function ApprovalRequestsPage() {
  const { user } = useAuth();
  const isMaster = user?.role === "master";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [dialog, setDialog] = useState(null); // { req, action: "approve"|"reject" }
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/bulk/approval-requests", { params });
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async () => {
    if (!dialog) return;
    setProcessing(true);
    try {
      const path = dialog.action === "approve" ? "approve" : "reject";
      const { data } = await api.post(`/bulk/approval-requests/${dialog.req.id}/${path}`, { note });
      if (dialog.action === "approve") {
        try { window.dispatchEvent(new CustomEvent("bulk-employees-updated", { detail: data })); } catch {}
        toast.success(`Pedido aprovado e executado. Batch: ${(data.batch_id || "").slice(0,8)}…`);
      } else {
        toast.success("Pedido rejeitado.");
      }
      setDialog(null); setNote("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setProcessing(false); }
  };

  return (
    <div className="space-y-4 pb-8" data-testid="approvals-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-[#065F46]" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">Administração</div>
            <h1 className="font-display text-[20px] font-semibold text-slate-900 leading-tight">
              Aprovações · Gestão em Lote
            </h1>
          </div>
        </div>
        <Button variant="outline" asChild className="h-9 border-slate-300">
          <Link to="/gestao-em-lote"><ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar</Link>
        </Button>
      </header>

      <div className="rounded-md border border-slate-200 bg-white p-3">
        <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="approvals-filters">
          {["pending", "executed", "rejected", "failed", "all"].map((s) => (
            <button key={s}
                    className={`h-7 px-3 rounded-md text-[11px] border ${statusFilter === s ? "bg-[#065F46] text-white border-[#065F46]" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}
                    onClick={() => setStatusFilter(s)}
                    data-testid={`filter-${s}`}>
              {s === "all" ? "Todos" : STATUS_META[s]?.label || s}
            </button>
          ))}
        </div>
        {!isMaster && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-2 text-[11.5px] text-blue-900 mb-3">
            Você vê apenas os pedidos que criou. Somente Master pode aprovar/rejeitar pedidos alheios.
          </div>
        )}
        {loading ? (
          <Skeleton className="h-24" />
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-[12px]" data-testid="empty-approvals">
            Nenhum pedido {statusFilter === "all" ? "" : STATUS_META[statusFilter]?.label.toLowerCase()}.
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((r) => {
              const st = STATUS_META[r.status] || { cls: "bg-slate-100 text-slate-700 border-slate-300", label: r.status };
              const isOwner = r.requested_by?.id === user?.id;
              return (
                <div key={r.id} className="rounded-md border border-slate-200 bg-white p-3 flex items-start gap-3"
                     data-testid={`approval-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${st.cls}`}>
                        {st.label}
                      </span>
                      <span className="text-[12px] font-mono text-slate-500">{r.id.slice(0, 8)}…</span>
                      <span className="text-[11px] text-slate-700 flex items-center gap-1">
                        <Users className="h-3 w-3" /> <b>{r.target_ids_count}</b> colaboradores
                      </span>
                    </div>
                    <div className="text-[11.5px] text-slate-700 mt-1">
                      <b>{r.reason}</b> · Solicitado por {r.requested_by?.name || r.requested_by?.email || "—"} em {fmtDateTime(r.created_at)}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {(r.updates || []).map((u, i) => (
                        <span key={i}>
                          <b>{u.field}</b> <code className="bg-slate-100 px-1 rounded">{u.mode}</code>
                          {u.mode !== "clear" && <> → <code className="bg-slate-100 px-1 rounded">{String(u.value ?? "")}</code></>}
                        </span>
                      ))}
                    </div>
                    {r.approved_by && (
                      <div className="text-[10.5px] text-emerald-700 mt-1">
                        Aprovado por {r.approved_by.name || r.approved_by.email}
                        {r.executed_batch_id && <> · Batch <code className="font-mono">{r.executed_batch_id.slice(0,8)}…</code></>}
                        {r.approval_note && <> · <i>"{r.approval_note}"</i></>}
                      </div>
                    )}
                    {r.rejected_by && (
                      <div className="text-[10.5px] text-rose-700 mt-1">
                        Rejeitado por {r.rejected_by.name || r.rejected_by.email}
                        {r.rejection_note && <> · <i>"{r.rejection_note}"</i></>}
                      </div>
                    )}
                  </div>
                  {r.status === "pending" && isMaster && !isOwner && (
                    <div className="flex flex-col gap-1">
                      <Button size="sm" onClick={() => { setDialog({ req: r, action: "approve" }); setNote(""); }}
                              className="h-7 text-[11px] bg-emerald-700 hover:bg-emerald-800 text-white"
                              data-testid={`btn-approve-${r.id}`}>
                        <Check className="h-3 w-3 mr-1" /> Aprovar e executar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setDialog({ req: r, action: "reject" }); setNote(""); }}
                              className="h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50"
                              data-testid={`btn-reject-${r.id}`}>
                        <X className="h-3 w-3 mr-1" /> Rejeitar
                      </Button>
                    </div>
                  )}
                  {r.status === "pending" && isMaster && isOwner && (
                    <Badge variant="outline" className="border-amber-300 text-amber-800 text-[10px]">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Aguardando outro Master
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {dialog && (
        <Dialog open onOpenChange={(v) => !v && setDialog(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className={dialog.action === "approve" ? "text-emerald-800" : "text-rose-700"}>
                {dialog.action === "approve" ? "Aprovar e executar" : "Rejeitar pedido"}
              </DialogTitle>
              <DialogDescription>
                {dialog.action === "approve"
                  ? `Ao aprovar, o lote de ${dialog.req.target_ids_count} colaboradores será executado imediatamente.`
                  : "Ao rejeitar, o pedido é encerrado sem alterações no banco."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-600">Observação (opcional)</label>
              <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                        placeholder="Motivo / justificativa da decisão"
                        data-testid="decision-note" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog(null)} disabled={processing}>Cancelar</Button>
              <Button onClick={decide} disabled={processing}
                      className={dialog.action === "approve"
                        ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                        : "bg-rose-600 hover:bg-rose-700 text-white"}
                      data-testid="btn-confirm-decision">
                {processing ? "Processando..." : dialog.action === "approve" ? "Aprovar e executar" : "Rejeitar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
